/* eslint-disable max-lines */
import {
  Component, ChangeDetectionStrategy, Input, 
  ChangeDetectorRef, ElementRef, EventEmitter, Output, SimpleChanges, ViewChild, OnChanges
} from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

import { TravelerTypes, TravelerInfoType } from '@pax/entities/traveler-info';
import { OptionSelected, Travelers } from '@booking/entities/booking-arrangement';
import { AirValidationResponse } from '@app/entities/air-validation.response';
import {
  BOOKING_FORM_INPUTS, GUEST_CHILDREN_RANGE_AGES, INFANTS_RANGE_AGES_IN_MONTH,
  INVALID, PASSENGER_CHILDREN_RANGE_AGES, TRAVELER_FORM_INPUTS
} from '@booking/constants/forms.constants';
import { PaxFormService } from '@pax/services/pax-form.service';
import { FormValidationService } from '@reservation/services/form-validation.service';
import { disabledPaxCountrySelectorMapper } from '@reservation/mappers/country-selector-disabled.mapper';
import { FrequentFlyerResponse } from '@app/entities/frequent-flyer';
import { FormDocumentInputComponent } from '@forms/form-components/components/form-document-input/form-document-input.component';
import { InputDateComponent } from '@forms/form-components/components/input-date/input-date.component';

import { User } from '@app/modules/core/interfaces/user';
import { CountriesVm } from '@booking/entities/countries';
import { FormInputType } from '@forms/form-text-input/entities/input-type';
import { TravelerForm, TravelerPax, TravelerPaxResponse, TravelerPaxResponseVm } from '@pax/entities/traveler-pax';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import {
  defaultPaxDocument, getPaxDocumentNameById, travelerFormDocumentValidators, internationalPaxDocumentFilledOut
} from '@forms/mappers/form-document-input.mapper';
import {
  BusinessRulesByBookingType, BookingPaxTypeDocumentProps, BookingPaxDocumentValidations
} from '@app/entities/booking-documents';
import { isCreatedNewPaxMapperByForm } from '@pax/mappers/pax-traveler-documents.mapper';
import { BookingDocumentPaxIdType } from '@app/entities/booking-pax-documents-id';
import { HotelProvider } from '@hotels/hotel-results/entities/hotel-searched';
import { PAX_FORM_PROPS } from '@pax/constants/pax-form-props.constants';
import { isNullOrUndefined } from '@app/util/util';


@Component({
  selector: 'app-form-passenger',
  templateUrl: './form-passenger.component.html',
  styleUrls: ['./form-passenger.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FormPassengerComponent implements OnChanges {

  @Input() frequentFlyerPrograms: FrequentFlyerResponse[] = [];
  @Input() showFrequentFlyer: boolean;
  @Input() validator: AirValidationResponse;

  @Input() form: UntypedFormGroup;
  @Input() paxForm: UntypedFormGroup;
  @Input() defaultFormData: TravelerForm;
  @Input() type: TravelerTypes;
  @Input() user: User;
  @Input() businessRules: BusinessRulesByBookingType;
  @Input() travelerBookingProvider: TravelBookingType;
  @Input() travelerInfoType: TravelerInfoType;
  @Input() countries: CountriesVm[] = [];
  @Input() productCode: string = null;
  @Input() productProvider: string = null;
  @Input() paxTraveler: TravelerPaxResponse;
  @Input() documents: BookingPaxTypeDocumentProps[] = [];
  @Input() bookingSource: HotelProvider;
  @Input() travelerAge: number | null = null;

  @Input() travelerNumber: number;

  @Input() isAnInternationalTrip: boolean;
  @Input() isCreateTraveler: boolean;
  @Input() isSubmitted: boolean;
  @Input() isSubmittedSuccess: boolean;
  @Input() isLoadingPaxForm: boolean;
  @Input() isRequired = true;
  @Input() disabledDocumentInputs: boolean;
  @Input() documentType: BookingDocumentPaxIdType;
  @Input() isMobile = false;

  @ViewChild('firstName') public firstNameRef: ElementRef;
  @ViewChild('secondName') public secondNameRef: ElementRef;
  @ViewChild('lastName') public lastNameRef: ElementRef;
  @ViewChild('documentNumber') public documentNumberRef: ElementRef;
  @ViewChild('genderRadio') public genderRef: ElementRef;
  @ViewChild('nationality') public nationalityRef: ElementRef;
  @ViewChild('dob') public dobRef: ElementRef;

  @ViewChild('documentSelectorRef', { read: FormDocumentInputComponent }) public documentSelectorRef: FormDocumentInputComponent;
  @ViewChild('dobInputRef', { read: InputDateComponent }) public dobInputRef: InputDateComponent;

  @Output() update: EventEmitter<TravelerPaxResponseVm> = new EventEmitter();
  @Output() create: EventEmitter<TravelerPax> = new EventEmitter();
  @Output() setCreatePaxUI: EventEmitter<boolean> = new EventEmitter();

  constructor(
    protected changeDetector: ChangeDetectorRef,
    protected paxFormService: PaxFormService,
    protected formValidationService: FormValidationService
  ) {}

  public genderStringSelector: string = TRAVELER_FORM_INPUTS.GENDER;
  public firstNameKey: string = TRAVELER_FORM_INPUTS.FIRST_NAME;
  public secondNameKey: string = TRAVELER_FORM_INPUTS.SECOND_NAME;
  public lastNameKey: string = TRAVELER_FORM_INPUTS.LAST_NAME;

  public onlyLettersInput: FormInputType = FormInputType.ONLY_LETTERS;
  public disabledCountryOptions = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.[PAX_FORM_PROPS.PAX_TRAVELER]) {
      if (!isNullOrUndefined(this.addDynamicElement.at(this.travelerNumber)) && !this.disabledDocumentInputs) {
        if (this.isRequired) {
          this.formValidationService.setPaxDocumentValidators(
            this.documentType, this.paxForm, this.travelerFormDocumentValidators);
        }
        if (this.documentSelectorRef) {
          this.documentSelectorRef.form = this.paxForm;
          this.documentSelectorRef.defaultDocument = this.defaultDocument;
          this.documentSelectorRef.documentType = this.documentType;
          this.documentSelectorRef.applyOrRemoveMask();
        }
      }
      this.disabledCountryOptions = disabledPaxCountrySelectorMapper(this.paxFormValue,
        this.businessRules, this.documents);
    }
    if (changes?.[PAX_FORM_PROPS.DOCUMENTS]) {
      if (this.documentSelectorRef && !this.disabledDocumentInputs) {
        this.documentSelectorRef.changeByCountry(
          getPaxDocumentNameById(this.documents, this.paxFormValue.ConfigurationDocumentId), this.defaultDocument);
      }
    }
  }

  get defaultDate(): string[] {
    return this.paxFormService.defaultDate(this.paxTraveler, this.user, this.travelerNumber, this.type);
  }

  get paxFormValue(): Travelers {
    return this.paxForm?.value as Travelers;
  }

  get hasPaxTravelerInfo(): boolean {
    return this.paxFormService.hasPaxTravelerInfo(this.paxTraveler);
  }

  get defaultDocument(): string {
    return defaultPaxDocument(this.documents, this.hasPaxTravelerInfo, this.hasUserInfo, this.paxForm, this.user);
  }

  get invalidDate(): boolean {
    return this.paxForm?.controls[TRAVELER_FORM_INPUTS.DOB]?.status === INVALID;
  }

  get travelerFormDocumentValidators(): BookingPaxDocumentValidations {
    return travelerFormDocumentValidators(this.paxForm, this.documents);
  }

  get hasUserInfo(): boolean {
    return this.paxFormService.hasUserInfo(this.user, this.travelerNumber, this.type);
  }

  get addDynamicElement(): UntypedFormArray {
    return this.form?.get(BOOKING_FORM_INPUTS.TRAVELERS) as UntypedFormArray;
  }

  get maxAges(): number {
    if (this.travelerInfoType === TravelerInfoType.GUEST) {
      return GUEST_CHILDREN_RANGE_AGES.MAX;
    } else {
      switch (this.type) {
        case TravelerTypes.CHILDREN:
          return this.travelerInfoType !== TravelerInfoType.PERSON ? PASSENGER_CHILDREN_RANGE_AGES.MAX: null;
        case TravelerTypes.INFANTS:
          return this.travelerInfoType !== TravelerInfoType.PERSON ? INFANTS_RANGE_AGES_IN_MONTH.MAX : null;
        default:
          return PASSENGER_CHILDREN_RANGE_AGES.MAX + 1;
      }
    }
  }

  get minAges(): number {
    if (this.travelerInfoType === TravelerInfoType.GUEST) {
      return GUEST_CHILDREN_RANGE_AGES.MIN;
    } else {
      switch (this.type) {
        case TravelerTypes.CHILDREN:
          return this.travelerInfoType !== TravelerInfoType.PERSON ? PASSENGER_CHILDREN_RANGE_AGES.MIN : null;
        case TravelerTypes.INFANTS:
          return this.travelerInfoType !== TravelerInfoType.PERSON ? INFANTS_RANGE_AGES_IN_MONTH.MIN : null;
        default:
          return null;
      }
    }
  }

  get yearsRangeForChildren(): number {
    switch (this.travelerBookingProvider) {
      case TravelBookingType.HOTELS:
      case TravelBookingType.PACKAGE_AIR:
        return GUEST_CHILDREN_RANGE_AGES.MAX;
      case TravelBookingType.ACTIVITIES:
        return null;
      default:
        return PASSENGER_CHILDREN_RANGE_AGES.MAX;
    }
  }

  get yearsRangeForInfants(): number {
    switch (this.travelerBookingProvider) {
      case TravelBookingType.HOTELS:
      case TravelBookingType.PACKAGE_AIR:
        return GUEST_CHILDREN_RANGE_AGES.MIN;
      case TravelBookingType.ACTIVITIES:
        return null;
      default:
        return PASSENGER_CHILDREN_RANGE_AGES.MIN;
    }
  }

  get defaultCode(): string {
    return this.defaultFormData[TRAVELER_FORM_INPUTS.NATIONALITY];
  }

  get activeDeleteFormData(): boolean {
    return !this.hasPaxTravelerInfo && this.hasUserInfo;
  }

  public scroll(elementRef: ElementRef): void {
    this.paxFormService.scrollIntoView(elementRef);
  }

  public setSaveFormData(): void {
    this.setCreatePaxUI.emit(!this.isCreateTraveler);
  }

  public save(): void {
    this.isSubmitted = true;
    this.changeDetector.detectChanges();
    this.validatePaxFieldsAbstract(this.paxForm);
    if (!this.formValidationService.formInvalidControls(this.paxForm)) {
      const values = this.paxForm.value;
      const paxTravelerInfo = this.paxFormService.mapperPaxData(values);
      this.isCreateNewPaxAbstract$(this.hasPaxTravelerInfo, this.documents, this.paxTraveler)
        .pipe(
          take(1),
          tap(isCreateNewPax => {
            if (isCreateNewPax) {
              this.create.emit(paxTravelerInfo);
            } else {
              this.update.emit({ ...paxTravelerInfo, _id: this.paxTraveler._id, documentId: this.paxTraveler.documentId });
            }
          })
        ).subscribe().unsubscribe();
    }
  }

  public deleteFormData(): void {
    this.paxFormService.deleteFormData(
      this.paxForm, this.defaultFormData, this.documentSelectorRef, this.dobInputRef, this.documents
    );
  }

  public changeUpperCaseGuestName(e: Event, type: string): void {
    this.paxFormService.changeUpperCaseName(e, type, this.paxForm);
  }

  public updateDate(date: Date): void {
    this.paxForm.patchValue({ [TRAVELER_FORM_INPUTS.DOB]: date });
  }

  private isCreateNewPaxAbstract$(
    hasInfo: boolean, documents: BookingPaxTypeDocumentProps[], pax: TravelerPaxResponse
  ): Observable<boolean> {
    return this.paxFormService.getPaxRegisterDocuments$(pax?._id).pipe(
      map(registerDocuments => isCreatedNewPaxMapperByForm(hasInfo, documents, pax, registerDocuments))
    );
  }

  private validatePaxFieldsAbstract(form: UntypedFormGroup): void {
    this.paxFormService.validateSectionFormToScroll(form, {
      [TRAVELER_FORM_INPUTS.FIRST_NAME]: this.firstNameRef,
      [TRAVELER_FORM_INPUTS.SECOND_NAME]: this.secondNameRef,
      [TRAVELER_FORM_INPUTS.LAST_NAME]: this.lastNameRef,
      [TRAVELER_FORM_INPUTS.DOCUMENT_ID]: this.documentNumberRef,
      [TRAVELER_FORM_INPUTS.GENDER]: this.genderRef,
      [TRAVELER_FORM_INPUTS.NATIONALITY]: this.nationalityRef,
      [TRAVELER_FORM_INPUTS.DOB]: this.dobRef
    });
  }

  public onDocumentTypeSelected(event: OptionSelected): void {
    if (this.paxForm?.get(TRAVELER_FORM_INPUTS.DOCUMENT_ID)) {
      this.paxForm?.patchValue({ [TRAVELER_FORM_INPUTS.DOCUMENT_ID]: event.value });

      if (this.paxForm?.get(TRAVELER_FORM_INPUTS.INTERNATIONAL_DOCUMENT_REQUIRED) && 
        (this.travelerInfoType === TravelerInfoType.PASSENGER || this.travelerInfoType === TravelerInfoType.PACKAGE_TRAVELER)) {
        this.paxForm?.patchValue({
          [TRAVELER_FORM_INPUTS.INTERNATIONAL_DOCUMENT_REQUIRED]: 
            internationalPaxDocumentFilledOut(this.documentType, this.isAnInternationalTrip)
        });
      }

      if (this.documents?.length) {
        this.disabledCountryOptions = disabledPaxCountrySelectorMapper(
          this.paxFormValue, this.businessRules, this.documents
        );
      }
      this.changeDetector.detectChanges();
    }
  }
}
